import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header";
import ManageColumns from "../../../components/filters/ManageColumns";
import { Modal, Tooltip, OverlayTrigger } from "react-bootstrap";
import { API_BASE_URL } from "../../../environment";
import Table from "../../../core/common/dataTable/index";
import { AuthContext } from "../../../context/authcontext";
import { DealService, DealRequest, Team } from "./deal.service";
import {
  showConfirmationAlert,
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import DealForm from "./dealForm";
import { DealFormDataProvider } from "./dealFormDataContext";
import {
  getLifecycleTypePluralName,
  getLifecycleTypeName,
} from "../../../utils/lifecycleUtils";
import male from "../../../../src/assets/male.png";
import TableFilters, {
  FilterState,
  FilterConfig,
  mergeTableFilterChanges,
} from "../../../components/filters/TableFilters";
import LifecycleFilterDropdown from "../components/LifecycleFilterDropdown";
import SortableHeader from "../../../components/filters/SortableHeader";
import SortByDropdown from "../../../components/filters/SortByDropdown";
import ListFilterChips, {
  getClearedListFilters,
} from "../components/ListFilterChips";
import TablePagination from "../../../components/pagination/TablePagination";
import { getAuthHeaders } from "../../../utils/authUtils";
import { useTenantChange } from "../../../hooks/useTenantChange";
import { getStageColor } from "../../../utils/stageColors";
import { sortRecords } from "../../../utils/sortUtils";
import ExportComponent from "../../../components/ExportComponent";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import EntityDetailsModal, {
  EntityDetailSection,
  EntityDetailLink,
  PriorityBadge,
} from "../../../components/EntityDetailsModal/EntityDetailsModal";
import { openOffcanvas } from "../../../utils/offcanvasUtils";
import { getApiErrorMessage } from "../../../utils/apiErrorUtils";
interface DealsProps {
  onViewSwitch?: (view: "list" | "kanban") => void;
}
const Deals = ({ onViewSwitch }: DealsProps) => {
  const navigate = useNavigate();
  const [owner, setOwner] = useState(["Collab"]);
  const [openModal, setOpenModal] = useState(false);
  const [deals, setDeals] = useState<any[]>([]);
  const [dealsCount, setDealsCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { selectedTenantId } = useContext(AuthContext);
  const [tenantUsers, setTenantUsers] = useState<any[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
    archivedFilter: "no",
  });

  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);

  const [addDealLoading, setAddDealLoading] = useState(false);
  const [addDealError, setAddDealError] = useState<string | null>(null);
  const [isAddDealOpen, setIsAddDealOpen] = useState(false);

  const [selectedDeal, setSelectedDeal] = useState<any | null>(null);
  const [detailsDeal, setDetailsDeal] = useState<any | null>(null);
  const [editDealLoading, setEditDealLoading] = useState(false);
  const [editDealError, setEditDealError] = useState<string | null>(null);

  const [selectedDealForDelete, setSelectedDealForDelete] = useState<
    any | null
  >(null);
  const [deleteDealLoading, setDeleteDealLoading] = useState(false);
  const [deleteDealError, setDeleteDealError] = useState<string | null>(null);

  const [resetFormTrigger, setResetFormTrigger] = useState(0);

  const clearDealsData = () => {
    setDeals([]);
    setDealsCount(null);
    setError(null);
    setCurrentPage(1);
    setPageSize(12);
    setTotalPages(1);
  };

  useTenantChange({
    onTenantChange: () => {
      clearDealsData();
    },
    refreshData: () => {
      if (selectedTenantId) {
      }
    },
    clearData: clearDealsData,
  });

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchField: "title",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      showStatusFilter: false,
      pageName: "deal" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      const mergedFilters = mergeTableFilterChanges(filters, newFilters);
      setFilters(mergedFilters);
      setCurrentPage(1);
      if (selectedTenantId) {
        fetchDeals(1, pageSize, mergedFilters);
      }
    },
    [filters, selectedTenantId, pageSize]
  );

  const handleDropdownFilterChange = useCallback(
    (dropdownFilters: Record<string, any>) => {
      const updatedFilters = {
        ...filters,
        ...dropdownFilters,
      };
      setFilters(updatedFilters);
      setCurrentPage(1);
      if (selectedTenantId) {
        fetchDeals(1, pageSize, updatedFilters);
      }
    },
    [filters, selectedTenantId, pageSize]
  );

  const applyListFilters = useCallback(
    (nextFilters: FilterState) => {
      setFilters(nextFilters);
      setCurrentPage(1);
      if (selectedTenantId) {
        fetchDeals(1, pageSize, nextFilters);
      }
    },
    [selectedTenantId, pageSize]
  );

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => { },
    []
  );

  const getDealsApiUrl = (
    page: number = 1,
    limit: number = 12,
    filterParams: Record<string, any> = {}
  ) => {
    if (!selectedTenantId) return "";

    let url = `${API_BASE_URL}/deal/?tenant_id=${selectedTenantId}&page=${page}&limit=${limit}`;

    const archivedFilter = filterParams.archivedFilter || "no";
    if (archivedFilter === "yes") {
      url += `&is_archive=true`;
    } else if (archivedFilter === "no") {
      url += `&is_archive=false`;
    }

    Object.keys(filterParams).forEach((key) => {
      if (["searchTerm", "dateRange", "statusFilter", "archivedFilter", "sortBy", "sortOrder", "industryFilter", "countryFilter"].includes(key)) {
        return;
      }
      if (filterParams[key] !== undefined && filterParams[key] !== null && filterParams[key] !== "") {
        url += `&${key}=${encodeURIComponent(filterParams[key])}`;
      }
    });

    if (filterParams.searchTerm) {
      url += `&title=${encodeURIComponent(filterParams.searchTerm)}`;
    }

    if (filterParams.dateRange && filterParams.dateRange[0]) {
      url += `&created_datetime_gte=${filterParams.dateRange[0].toISOString().split("T")[0]
        }`;
    }

    if (filterParams.dateRange && filterParams.dateRange[1]) {
      url += `&created_datetime_lte=${filterParams.dateRange[1].toISOString().split("T")[0]
        }`;
    }

    if (filterParams.statusFilter) {
      url += `&lifecycle_type_stage_id=${encodeURIComponent(
        filterParams.statusFilter
      )}`;
    }

    const authRaw = localStorage.getItem("authResponse");
    if (!authRaw) return url;

    try {
      const auth = JSON.parse(authRaw);

      const tenantObj = Array.isArray(auth.tenants)
        ? auth.tenants.find(
          (t: any) => String(t.tenant_id) === selectedTenantId
        )
        : null;

      if (tenantObj?.role_key === "SALESUSER" && auth.user_id) {
        url += `&sales_user_id=${auth.user_id}`;
      }
    } catch (error) {
      console.error("Error parsing auth data:", error);
    }

    return url;
  };

  const fetchDeals = useCallback(
    async (
      page: number = 1,
      limit: number = 12,
      filterParams: Record<string, any> = {}
    ) => {
      if (!selectedTenantId) return;

      setLoading(true);
      setError(null);
      try {
        const authHeader = localStorage.getItem("authCred") || "";

        const response = await fetch(
          getDealsApiUrl(page, limit, filterParams),
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch deals");
        const data = await response.json();

        const dealsArray = Array.isArray(data.results)
          ? data.results
          : Array.isArray(data)
            ? data
            : [];

        setDeals(dealsArray);
        setDealsCount(
          typeof data.count === "number" ? data.count : dealsArray.length
        );

        const totalCount =
          typeof data.count === "number" ? data.count : dealsArray.length;
        setTotalPages(Math.ceil(totalCount / limit));

        setBackgroundLoading(true);
      } catch (err) {
        console.error("Error fetching deals:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch deals");
      } finally {
        setLoading(false);
      }
    },
    [selectedTenantId]
  );

  const handlePageChange = useCallback(
    (page: number, size?: number) => {
      const newPageSize = size || pageSize;
      setCurrentPage(page);
      if (size) setPageSize(newPageSize);

      setStars({});

      fetchDeals(page, newPageSize, filters);
    },
    [pageSize, filters]
  );

  const handleSortChange = useCallback(
    (newSortBy: string, newOrder: "asc" | "desc") => {
      const newFilters = {
        ...filters,
        sortBy: newSortBy,
        sortOrder: newOrder,
      };
      setFilters(newFilters);
    },
    [filters]
  );

  useEffect(() => {
    if (selectedTenantId) {
      fetchDeals(1, pageSize);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    if (backgroundLoading) {
      const fetchBackgroundData = async () => {
        try {
          const teamList = await DealService.getTeams(Number(selectedTenantId));
          setTeams(teamList || []);
        } catch (e) {
          console.error("Background API error:", e);
        } finally {
          setBackgroundLoading(false);
        }
      };

      fetchBackgroundData();
    }
  }, [backgroundLoading, selectedTenantId]);

  const route = all_routes;
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };
  const [selectedDate2, setSelectedDate2] = useState<Date | null>(new Date());
  const handleDateChange2 = (date: Date | null) => {
    setSelectedDate2(date);
  };

  const [stars, setStars] = useState<{ [key: number]: boolean }>({});

  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const defaultColumnVisibility = {
    title: true,
    priority: true,
    assigned_user: true,
    related_lead: true,
    status: true,
    assigned_team: false,
    primary_contact: false,
    related_pipeline: false,
    source: false,
    currency_code: false,
    company: true,
    estimated_value: false,
  };

  const [columnVisibility, setColumnVisibility] = useState<{
    [key: string]: boolean;
  }>(() => {
    try {
      const saved = localStorage.getItem("deals_column_visibility");
      if (saved) {
        const parsed = JSON.parse(saved);
        const visibility: { [key: string]: boolean } = {
          ...defaultColumnVisibility,
        };
        Object.keys(parsed).forEach((key) => {
          if (key in defaultColumnVisibility) {
            visibility[key] = parsed[key] === true;
          }
        });
        const visibleCount = Object.values(visibility).filter(Boolean).length;
        if (visibleCount === 0) {
          const defaultKeys = Object.keys(defaultColumnVisibility);
          for (const key of defaultKeys) {
            if (
              defaultColumnVisibility[
              key as keyof typeof defaultColumnVisibility
              ]
            ) {
              visibility[key] = true;
              break;
            }
          }
        }
        return visibility;
      }
    } catch (error) {
      console.error(
        "Error loading column visibility from localStorage:",
        error
      );
    }
    return defaultColumnVisibility;
  });

  const saveColumnVisibilityToStorage = useCallback(
    (visibility: { [key: string]: boolean }) => {
      try {
        localStorage.setItem(
          "deals_column_visibility",
          JSON.stringify(visibility)
        );
      } catch (error) {
        console.error(
          "Error saving column visibility to localStorage:",
          error
        );
      }
    },
    []
  );

  const availableColumns = [
    { key: "title", label: "Title" },
    { key: "company", label: "Company" },
    { key: "priority", label: "Priority" },
    { key: "assigned_user", label: "Assigned User" },
    { key: "related_lead", label: "Related Lead" },
    { key: "status", label: "Status" },
    { key: "estimated_value", label: "Estimated Value" },
    { key: "assigned_team", label: "Assigned Team" },
    { key: "primary_contact", label: "Contact Name" },
    { key: "related_pipeline", label: "Related Pipeline" },
    { key: "source", label: "Source" },
    { key: "currency_code", label: "Currency" },

  ];

  const [orderedAvailableColumns, setOrderedAvailableColumns] = useState(() => {
    const storageKey = `${window.location.pathname}_column_order`;
    const savedOrder = localStorage.getItem(storageKey);
    if (savedOrder) {
      try {
        const orderKeys = JSON.parse(savedOrder);
        return [...availableColumns].sort((a, b) => {
          const indexA = orderKeys.indexOf(a.key);
          const indexB = orderKeys.indexOf(b.key);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      } catch (e) {
        return [...availableColumns];
      }
    }
    return [...availableColumns];
  });

  const getSortedAvailableColumns = () => {
    return orderedAvailableColumns;
  };

  const handleColumnVisibilityChange = (
    columnKey: string,
    isVisible: boolean
  ) => {
    const currentVisibleCount =
      Object.values(columnVisibility).filter(Boolean).length;
    if (isVisible && currentVisibleCount >= 6) {
      showErrorAlert(
        "Maximum 6 columns can be enabled at once. Please disable another column first."
      );
      return;
    }
    if (!isVisible && currentVisibleCount <= 1) {
      showErrorAlert("At least one column must remain visible.");
      return;
    }
    const newColumnVisibility = {
      ...columnVisibility,
      [columnKey]: isVisible,
    };
    setColumnVisibility(newColumnVisibility);
    saveColumnVisibilityToStorage(newColumnVisibility);
  };

  const handleResetColumns = () => {
    setColumnVisibility(defaultColumnVisibility);
    saveColumnVisibilityToStorage(defaultColumnVisibility);
  };

  const initializeStarsState = () => {
    const starsState: { [key: number]: boolean } = {};
    deals.forEach((item, index) => {
      starsState[index] = false;
    });
    setStars(starsState);
  };

  React.useEffect(() => {
    initializeStarsState();
  }, [deals]);
  const handleStarToggle = (index: number) => {
    setStars((prevStars) => ({
      ...prevStars,
      [index]: !prevStars[index],
    }));
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const offcanvas = document.getElementById(offcanvasId);
    if (offcanvas) {
      try {
        if ((window as any).bootstrap?.Offcanvas) {
          const bsOffcanvas = (window as any).bootstrap.Offcanvas.getInstance(
            offcanvas
          );
          if (bsOffcanvas) {
            bsOffcanvas.hide();
          } else {
            const newOffcanvas = new (window as any).bootstrap.Offcanvas(
              offcanvas
            );
            newOffcanvas.hide();
          }
        } else {
          const closeButton = offcanvas.querySelector(
            '[data-bs-dismiss="offcanvas"]'
          ) as HTMLElement;
          if (closeButton) {
            closeButton.click();
          } else {
            offcanvas.classList.remove("show");
            const backdrop = document.querySelector(".offcanvas-backdrop");
            if (backdrop) backdrop.remove();
            document.body.classList.remove("offcanvas-open");
            document.body.style.removeProperty("overflow");
            document.body.style.removeProperty("padding-right");
          }
        }
      } catch (error) {
        console.warn("Error closing offcanvas:", error);
      }
    }
  };

  useEffect(() => {
    const addOffcanvas = document.getElementById("offcanvas_add_deal");

    const handleHidden = () => {
      setIsAddDealOpen(false);
    };

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (addOffcanvas) {
        addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  useEffect(() => {
    saveColumnVisibilityToStorage(columnVisibility);
  }, [columnVisibility, saveColumnVisibilityToStorage]);

  const handleAddDeal = async (dealData: Partial<DealRequest>) => {
    setAddDealLoading(true);
    setAddDealError(null);

    try {
      await DealService.createDeal(dealData);

      setTimeout(() => {
        setResetFormTrigger((prev) => prev + 1);
      }, 100);

      fetchDeals(currentPage, pageSize, filters);
      closeOffcanvas("offcanvas_add_deal");

      setTimeout(() => {
        showSuccessAlert(
          `${getLifecycleTypeName("DEAL")} created successfully!`
        );
      }, 500);
    } catch (error: any) {
      console.error("Error creating deal:", error);
      setAddDealError(getApiErrorMessage(error, "Failed to create deal"));
      showErrorAlert(getApiErrorMessage(error, "Failed to create deal"));
      throw error;
    } finally {
      setAddDealLoading(false);
    }
  };

  const handleEditDeal = async (dealData: Partial<DealRequest>) => {
    if (!selectedDeal) return;

    setEditDealLoading(true);
    setEditDealError(null);

    try {
      await DealService.updateDeal(selectedDeal.deal_id, dealData);

      fetchDeals(currentPage, pageSize, filters);
      closeOffcanvas("offcanvas_edit");
      setSelectedDeal(null);

      setTimeout(() => {
        showSuccessAlert(
          `${getLifecycleTypeName("DEAL")} updated successfully!`
        );
      }, 500);
    } catch (error: any) {
      console.error("Error updating deal:", error);
      setEditDealError(getApiErrorMessage(error, "Failed to update deal"));
      showErrorAlert(getApiErrorMessage(error, "Failed to update deal"));
      throw error;
    } finally {
      setEditDealLoading(false);
    }
  };

  const handleEditClick = (deal: any) => {
    flushSync(() => {
      setSelectedDeal(deal);
      setEditDealError(null);
    });
  };

  const handleDeleteDeal = async (deal: any) => {
    if (!selectedTenantId) return;

    const result = await showConfirmationAlert(
      "Delete Deal",
      `Are you sure you want to delete "${deal.title}"? This action cannot be undone.`,
      async () => {
        await DealService.deleteDeal(
          deal.deal_id,
          Number(selectedTenantId)
        );
      }
    );

    if (result.isConfirmed) {
      await showSuccessAlert(
        `${getLifecycleTypeName("DEAL")} deleted successfully!`
      );
      fetchDeals(currentPage, pageSize, filters);
    }
  };

  const handleBulkDelete = async () => {
    const selectedIndices = Object.keys(stars)
      .filter((key) => stars[parseInt(key)])
      .map((key) => parseInt(key));
    if (selectedIndices.length === 0) return;

    const result = await showConfirmationAlert(
      "Delete Selected Records",
      `Are you sure you want to delete ${selectedIndices.length} selected record${selectedIndices.length > 1 ? "s" : ""}? This action cannot be undone.`,
      async () => {
        setIsBulkDeleting(true);
        try {
          const selectedDeals = selectedIndices
            .map((index) => paginatedDeals[index])
            .filter((deal) => deal);

          const deletePromises = selectedDeals.map((deal) =>
            DealService.deleteDeal(deal.deal_id, Number(selectedTenantId))
          );
          await Promise.all(deletePromises);
        } finally {
          setIsBulkDeleting(false);
        }
      }
    );

    if (result.isConfirmed && result.value) {
      setStars({});

      await showSuccessAlert(
        `${selectedIndices.length} ${getLifecycleTypeName(
          "DEAL"
        ).toLowerCase()}${selectedIndices.length > 1 ? "s" : ""
        } deleted successfully!`
      );

      fetchDeals(currentPage, pageSize, filters);
    }
  };;

  const handlePreviewClick = (deal: any) => {
    window.location.href = `/deals/${deal.deal_id}`;
  };
  const pipelineOption = [
    { value: "choose", label: "Choose" },
    { value: "sales", label: "Sales" },
    { value: "marketing", label: "Marketing" },
    { value: "calls", label: "Calls" },
  ];
  const status = [
    { value: "choose", label: "Choose" },
    { value: "Open", label: "Open" },
    { value: "Lost", label: "Lost" },
    { value: "Won", label: "Won" },
  ];
  const currencys = [
    { value: "Select", label: "Select" },
    { value: "$", label: "$" },
    { value: "€", label: "€" },
  ];

  const duration = [
    { value: "Choose", label: "Choose" },
    { value: "Days", label: "Days" },
    { value: "Month", label: "Month" },
  ];

  const socialMedia = [
    { value: "facebook", label: "Facebook" },
    { value: "twitter", label: "Twitter" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "instagram", label: "Instagram" },
    { value: "other", label: "Other" },
  ];

  const paginatedDeals = useMemo(
    () =>
      sortRecords<any>(deals, filters.sortBy, filters.sortOrder, {
        assigned_team: (record) =>
          record.assigned_team?.team_name ||
          teams.find((t) => t.team_id === record.assigned_team_id)?.team_name,
      }),
    [deals, filters.sortBy, filters.sortOrder, teams]
  );

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setStars((prev) => {
      const newStars = { ...prev };
      paginatedDeals.forEach((_: any, idx: number) => {
        newStars[idx] = checked;
      });
      return newStars;
    });
  };

  React.useEffect(() => {
    setStars({});
  }, [currentPage, filters.sortBy, filters.sortOrder]);

  const userOptions = tenantUsers.map((u) => ({
    value: u.user_id,
    label: `${u.user_first_name.trim()} ${u.user_last_name.trim()}`,
  }));

  const allAvailableColumns = useMemo(
    () => [
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`${getLifecycleTypeName("DEAL")} Title`}
              field="title"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "title",
        key: "title",
        render: (text: string, record: any) => (
          <div>
            <div>
              <Link
                to={`/deals/${record.deal_id}`}
                state={{ dealData: record }}
                className="title-name"
              >
                {record.title?.length > 40 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-${record.deal_id}`}>{record.title}</Tooltip>}
                  >
                    <span>{`${record.title.substring(0, 40)}...`}</span>
                  </OverlayTrigger>
                ) : (
                  record.title
                )}
              </Link>
            </div>
            <span className="text-muted small">
              {record.company?.company_name || "-"}
            </span>
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Priority"
              field="priority"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "priority",
        key: "priority",
        render: (priority: string, record: any) => {
          const getPriorityIcon = (val: string) => {
            switch (val?.toLowerCase()) {
              case "high":
                return <i className="ti ti-square-rounded-filled me-1 text-danger circle" />;
              case "medium":
                return <i className="ti ti-square-rounded-filled me-1 text-secondary circle" />;
              case "low":
                return <i className="ti ti-square-rounded-filled me-1 text-info circle" />;
              default:
                return <i className="ti ti-square-rounded-filled me-1 text-secondary circle" />;
            }
          };
          if (!record.priority) return <div className="text-center fw-bold">-</div>;
          return (
            <span className="text-default">
              {getPriorityIcon(record.priority)}
              {record.priority}
            </span>
          );
        },
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Company"
              field="company"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "company",
        key: "company",
        render: (company: any, record: any) => (
          <div className={!record.company?.company_name ? "text-center fw-bold" : ""}>
            {record.company?.company_name || "-"}
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Estimated Value"
              field="estimated_value"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "estimated_value",
        key: "estimated_value",
        render: (value: number, record: any) => (
          <div className={!value ? "text-center fw-bold" : ""}>
            {value ? `${record.currency_code || "$"} ${Number(value || 0).toFixed(4)}` : "-"}
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Assigned Team"
              field="assigned_team"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "assigned_team",
        key: "assigned_team",
        render: (text: string, record: any) => (
          <>
            {record.assigned_team?.team_name
              ? record.assigned_team.team_name
              : (() => {
                const team = teams.find(
                  (t) => t.team_id === record.assigned_team_id
                );
                return team ? team.team_name : "No Team Assigned";
              })()}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Assigned User"
              field="assigned_user"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "assigned_user",
        key: "assigned_user",
        render: (text: string, record: any) => (
          <>
            {record.assigned_user ? (
              <div className="d-flex align-items-center">
                {record.assigned_user.avatar_url ? (
                  <img
                    src={record.assigned_user.avatar_url}
                    alt={record.assigned_user.full_name}
                    className="me-2 rounded-circle"
                    style={{ width: 24, height: 24, objectFit: "cover" }}
                  />
                ) : (
                  <img
                    src={male}
                    alt="Default Avatar"
                    className="me-2 rounded-circle"
                    style={{ width: 24, height: 24, objectFit: "cover" }}
                  />
                )}
                <span>{record.assigned_user.full_name}</span>
              </div>
            ) : (
              "No User Assigned"
            )}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Contact Name"
              field="primary_contact"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "primary_contact",
        key: "primary_contact",
        render: (_: string, record: any) => (
          <div>
            {record.primary_contact?.full_name && (
              <div>{record.primary_contact.full_name}</div>
            )}
            <span className="text-muted small">
              {record.primary_contact?.primary_phone || "-"}
            </span>
          </div>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`Related ${getLifecycleTypeName("LEAD")}`}
              field="realted_lead"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "related_lead",
        key: "related_lead",
        render: (text: string, record: any) => (
          <>
            {record.realted_lead?.lead_id ? (
              <Link
                to={`/leads/${record.realted_lead.lead_id}`}
                className="crm-link"
                target="_blank"
              >
                {record.realted_lead.title?.length > 35 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-lead-${record.realted_lead.lead_id}`}>{record.realted_lead.title}</Tooltip>}
                  >
                    <span>{`${record.realted_lead.title.substring(0, 35)}...`}</span>
                  </OverlayTrigger>
                ) : (
                  record.realted_lead.title || "-"
                )}
              </Link>
            ) : record.realted_lead?.title?.length > 35 ? (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id={`tooltip-lead-title-${record.deal_id}`}>{record.realted_lead.title}</Tooltip>}
              >
                <span>{`${record.realted_lead.title.substring(0, 35)}...`}</span>
              </OverlayTrigger>
            ) : (
              record.realted_lead?.title || "-"
            )}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`Related ${getLifecycleTypeName("PIPELINE")}`}
              field="related_pipeline"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "related_pipeline",
        key: "related_pipeline",
        render: (text: string, record: any) => (
          <>
            {record.related_pipeline?.pipeline_id ? (
              <Link
                to={`/pipelines/${record.related_pipeline.pipeline_id}`}
                className="crm-link"
                target="_blank"
              >
                {record.related_pipeline.title?.length > 35 ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-pipeline-${record.related_pipeline.pipeline_id}`}>{record.related_pipeline.title}</Tooltip>}
                  >
                    <span>{`${record.related_pipeline.title.substring(0, 35)}...`}</span>
                  </OverlayTrigger>
                ) : (
                  record.related_pipeline.title || "-"
                )}
              </Link>
            ) : record.related_pipeline?.title?.length > 35 ? (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id={`tooltip-pipeline-title-${record.deal_id}`}>{record.related_pipeline.title}</Tooltip>}
              >
                <span>{`${record.related_pipeline.title.substring(0, 35)}...`}</span>
              </OverlayTrigger>
            ) : (
              record.related_pipeline?.title || "-"
            )}
          </>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title={`${getLifecycleTypeName("DEAL")} Status`}
              field="lifecycle_type_stage"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "status",
        key: "status",
        render: (text: string, record: any) => (
          <span
            className={`badge badge-pill badge-status ${getStageColor(
              record.lifecycle_type_stage?.stage_name || "",
              record.lifecycle_type_stage?.lifecycle_type_stage_id,
              record.lifecycle_type_stage?.stage_order
            )} no-border`}
            style={{ border: "none", borderColor: "transparent" }}
          >
            {record.lifecycle_type_stage?.stage_name || "-"}
          </span>
        ),
      },
      {
        title: (
          <div className="table-header-with-divider">
            <SortableHeader
              title="Source"
              field="source"
              currentSort={filters.sortBy}
              currentOrder={filters.sortOrder}
              onSort={handleSortChange}
            />
            <div className="column-divider"></div>
          </div>
        ),
        dataIndex: "source",
        key: "source",
        render: (text: string, record: any) => (
          <div className={!record.source ? "text-center fw-bold" : ""}>{record.source || "-"}</div>
        ),
      },
      {
        title: (
          <SortableHeader
            title="Currency"
            field="currency_code"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        ),
        dataIndex: "currency_code",
        key: "currency_code",
        render: (text: string, record: any) => (
          <div className={!record.currency_code ? "text-center fw-bold" : ""}>{record.currency_code || "-"}</div>
        ),
      },
    ],
    [filters.sortBy, filters.sortOrder, handleSortChange, teams]
  );

  const buildDealSections = useCallback(
    (deal: any): EntityDetailSection[] => {
      return [
        {
          title: `${getLifecycleTypeName("DEAL")} Info`,
          icon: "ti ti-medal",
          fields: [
            { label: "Title", value: deal.title, fullWidth: true },
            {
              label: "Priority",
              value: <PriorityBadge priority={deal.priority} />,
            },
            {
              label: "Status",
              value: deal.lifecycle_type_stage?.stage_name,
            },
            { label: "Source", value: deal.source },
          ],
        },
        {
          title: "Company & Contact",
          icon: "ti ti-building",
          fields: [
            { label: "Company", value: deal.company?.company_name },
            {
              label: "Contact Name",
              value: deal.primary_contact?.full_name,
            },
            {
              label: "Contact Phone",
              value: deal.primary_contact?.primary_phone,
            },
          ],
        },
        {
          title: "Related Records",
          icon: "bi bi-link-45deg",
          fields: [
            {
              label: `Related ${getLifecycleTypeName("LEAD")}`,
              value: deal.realted_lead?.lead_id ? (
                <EntityDetailLink
                  href={`/leads/${deal.realted_lead.lead_id}`}
                  label={deal.realted_lead.title}
                />
              ) : (
                deal.realted_lead?.title
              ),
            },
            {
              label: `Related ${getLifecycleTypeName("PIPELINE")}`,
              value: deal.related_pipeline?.pipeline_id ? (
                <EntityDetailLink
                  href={`/pipelines/${deal.related_pipeline.pipeline_id}`}
                  label={deal.related_pipeline.title}
                />
              ) : (
                deal.related_pipeline?.title
              ),
            },
          ],
        },
        {
          title: "Ownership",
          icon: "ti ti-users",
          fields: [
            {
              label: "Assigned Team",
              value:
                deal.assigned_team?.team_name || deal.assigned_team?.name,
            },
            { label: "Assigned User", value: deal.assigned_user?.full_name },
            { label: "Currency", value: deal.currency_code },
          ],
        },
        {
          title: "Timestamps",
          icon: "ti ti-clock",
          fields: [
            {
              label: "Created",
              value: deal.created_datetime
                ? formatCustomDateTime(deal.created_datetime)
                : null,
            },
            {
              label: "Updated",
              value: deal.updated_datetime
                ? formatCustomDateTime(deal.updated_datetime)
                : null,
            },
          ],
        },
      ];
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        title: (
          <div className="form-check form-check-md">
            <input
              className="form-check-input"
              type="checkbox"
              checked={
                !!paginatedDeals.every((_: any, idx: number) => stars[idx])
              }
              onChange={handleSelectAll}
            />
          </div>
        ),
        dataIndex: "selection",
        render: (_: any, record: any, index: number) => (
          <div className="form-check form-check-md">
            <input
              className="form-check-input"
              type="checkbox"
              checked={!!stars[index]}
              onChange={() => handleStarToggle(index)}
            />
          </div>
        ),
      },
      ...getSortedAvailableColumns()
        .filter((col) => columnVisibility[col.key])
        .map((col) =>
          allAvailableColumns.find((allCol) => allCol.key === col.key)
        )
        .filter(Boolean),
      {
        title: "Actions",
        dataIndex: "actions",
        render: (text: string, record: any) => (
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Link
              to="#"
              className="action-icon"
              onClick={(e) => {
                e.preventDefault();
                setDetailsDeal(record);
              }}
            >
              <i className="ti ti-eye text-blue-light" />
            </Link>
            <div className="dropdown table-action">
              <Link
                to="#"
                className="action-icon"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                data-bs-boundary="viewport"
                data-bs-popper-config='{"strategy":"fixed"}'
              >
                <i className="fa fa-ellipsis-v" />
              </Link>
              <div className="dropdown-menu dropdown-menu-end">
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePreviewClick(record);
                  }}
                >
                  <i className="ti ti-info-circle text-blue-light" /> View Details
                </Link>
                <Link
                  className="dropdown-item edit-popup"
                  to="#"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvas_edit"
                  onClick={() => handleEditClick(record)}
                >
                  <i className="ti ti-edit text-blue" /> Edit
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeleteDeal(record);
                  }}
                >
                  <i className="ti ti-trash text-danger" /> Delete
                </Link>
              </div>
            </div>
          </div>
        ),
      },
    ],
    [
      columnVisibility,
      paginatedDeals,
      stars,
      handleSelectAll,
      handleStarToggle,
      getSortedAvailableColumns,
      allAvailableColumns,
      handleEditClick,
      handleDeleteDeal,
      handlePreviewClick,
    ]
  );

  return (
    <DealFormDataProvider isMainApiCompleted={!loading} shouldLoad={isAddDealOpen || !!selectedDeal}>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  {" "}
                  <div className="col-8">
                    <h4 className="page-title">
                      {getLifecycleTypePluralName("DEAL")}
                      <span className="count-title">{dealsCount || 0}</span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col">
                      <ListFilterChips
                        entityType="deal"
                        filters={filters}
                        onRemove={(patch) => applyListFilters({ ...filters, ...patch })}
                        onClearAll={() => applyListFilters(getClearedListFilters())}
                        disabled={loading}
                        className="mb-0"
                      />
                    </div>
                    <div className="col-sm-auto">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        {Object.values(stars).some(Boolean) && (
                          <button
                            type="button"
                            className="btn btn-danger ms-3"
                            onClick={handleBulkDelete}
                            disabled={isBulkDeleting}
                          >
                            <i className="ti ti-trash me-2" />
                            Delete Selected (
                            {Object.values(stars).filter(Boolean).length})
                          </button>
                        )}{" "}
                        &nbsp; &nbsp;
                        <ExportComponent entityType="deal" filters={filters} />
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add_deal"
                          onClick={() => setIsAddDealOpen(true)}
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add {getLifecycleTypeName("DEAL")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <TableFilters
                      config={filterConfig}
                      data={deals}
                      onFilterChange={handleFilterChange}
                      onApiFilterChange={handleApiFilterChange}
                      sortBy={filters.sort_by}
                      searchTerm={filters.searchTerm}
                      dateRange={filters.dateRange}
                      hideClearButton
                      tenantId={selectedTenantId || undefined}
                      className="mt-4"
                    />
                  </div>
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <SortByDropdown
                      value={filters.sort_by}
                      onChange={(sortBy) =>
                        setFilters((prev) => ({ ...prev, sort_by: sortBy }))
                      }
                    />
                    <span className="mx-2">
                      <LifecycleFilterDropdown
                        entityType="deal"
                        onFilterChange={handleDropdownFilterChange}
                        currentFilters={{ ...filters }}
                        loading={loading}
                      />
                    </span>
                    <ManageColumns
                      availableColumns={availableColumns}
                      columnVisibility={columnVisibility}
                      onColumnVisibilityChange={handleColumnVisibilityChange}
                      onResetColumns={handleResetColumns}
                      onReorderColumns={setOrderedAvailableColumns}
                    />
                    <div className="view-icons ms-3">
                      <Link to="#" className="active">
                        <i className="ti ti-list-tree" />
                      </Link>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          onViewSwitch?.("kanban");
                        }}
                      >
                        <i className="ti ti-grid-dots" />
                      </Link>
                    </div>
                  </div>
                </div>
                {loading ? (
                  <div className="card-body">
                    <div
                      className="d-flex flex-column align-items-center justify-content-center"
                      style={{ minHeight: "60vh" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">
                          Loading {getLifecycleTypePluralName("DEAL")}...
                        </small>
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div className="card-body">
                    <div className="alert alert-danger text-center">
                      <i className="ti ti-alert-circle me-2"></i>
                      {error}
                    </div>
                  </div>
                ) : (
                  <div className="card-body">
                    <div
                      className="table-responsive contact-table-wrapper"
                      style={{ position: "relative" }}
                    >
                      {isFilterLoading && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                            minHeight: "200px",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <span>Filtering results...</span>
                          </div>
                        </div>
                      )}
                      {paginatedDeals.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                            <i className="ti ti-medal fs-36 text-gray"></i>
                          </div>
                          <h4 className="text-gray mb-2">
                            No {getLifecycleTypePluralName("DEAL")} Found!
                          </h4>
                          <p className="text-muted mb-3">
                            There are no {getLifecycleTypePluralName("DEAL")} to
                            display at the moment.
                          </p>
                          <button
                            className="btn btn-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#offcanvas_add_deal"
                            onClick={() => setIsAddDealOpen(true)}
                          >
                            <i className="fa fa-plus me-2" />
                            Add Your First {getLifecycleTypeName("DEAL")}
                          </button>
                        </div>
                      ) : (
                        <>
                          <Table
                            columns={columns}
                            dataSource={paginatedDeals}
                            rowKey={(record: any) =>
                              record.deal_id || record.id
                            }
                            rowSelection={false}
                            className="table table-striped contact-table"
                            pagination={false}
                          />
                          <TablePagination
                            currentPage={currentPage}
                            pageSize={pageSize}
                            totalItems={dealsCount || 0}
                            filteredItems={dealsCount || 0}
                            isFiltered={
                              filters.searchTerm !== "" ||
                              filters.dateRange[0] !== null ||
                              filters.dateRange[1] !== null
                            }
                            onPageChange={handlePageChange}
                            onPageSizeChange={(
                              current: number,
                              size: number
                            ) => {
                              setCurrentPage(1);
                              setPageSize(size);
                              setStars({});
                              fetchDeals(1, size, filters);
                            }}
                            entityName={getLifecycleTypePluralName("DEAL")}
                            loading={loading}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */} {/* Add New Deals Off-canvas */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_add_deal"
        style={{ width: "550px" }}
      >
        {" "}
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">
            Add New {getLifecycleTypeName("DEAL")}
          </h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {addDealError && (
            <div className="alert alert-danger" role="alert">
              {addDealError}
            </div>
          )}
          {isAddDealOpen && (
            <DealForm
              key={`create-new-deal-${resetFormTrigger}`}
              onSubmit={handleAddDeal}
              isSubmitting={addDealLoading}
              resetTrigger={resetFormTrigger}
              onCancel={() => {
                const offcanvas = document.getElementById("offcanvas_add_deal");
                if (offcanvas) {
                  try {
                    if ((window as any).bootstrap?.Offcanvas) {
                      const bsOffcanvas = (
                        window as any
                      ).bootstrap.Offcanvas.getInstance(offcanvas);
                      if (bsOffcanvas) {
                        bsOffcanvas.hide();
                      }
                    } else {
                      const closeButton = offcanvas.querySelector(
                        '[data-bs-dismiss="offcanvas"]'
                      ) as HTMLElement;
                      if (closeButton) {
                        closeButton.click();
                      }
                    }
                  } catch (error) {
                    console.warn("Error closing add offcanvas:", error);
                    const closeButton = offcanvas.querySelector(
                      '[data-bs-dismiss="offcanvas"]'
                    ) as HTMLElement;
                    if (closeButton) {
                      closeButton.click();
                    }
                  }
                }
              }}
            />
          )}
        </div>
      </div>
      {/* /Add New Deals Off-canvas */} {/* Edit Deals */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_edit"
        style={{ width: "550px" }}
      >
        {" "}
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Edit {getLifecycleTypeName("DEAL")}</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {editDealError && (
            <div className="alert alert-danger" role="alert">
              {editDealError}
            </div>
          )}
          {selectedDeal && (
            <DealFormDataProvider isMainApiCompleted={true}>
              <DealForm
                deal={selectedDeal}
                onSubmit={handleEditDeal}
                isSubmitting={editDealLoading}
                onCancel={() => {
                  closeOffcanvas("offcanvas_edit");
                  setSelectedDeal(null);
                }}
                isMainApiCompleted={true}
              />
            </DealFormDataProvider>
          )}
        </div>
      </div>
      {/* /Edit Deals */}
      {/* Create Deal Success Modal */}
      <Modal show={openModal} onHide={() => setOpenModal(false)}>
        <div className="modal-header border-0 m-0 justify-content-end">
          <button
            className="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => setOpenModal(false)}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="modal-body">
          <div className="success-message text-center">
            <div className="success-popup-icon bg-light-blue">
              <i className="ti ti-medal" />
            </div>
            <h3>Deal Created Successfully!!!</h3>
            <p>View the details of deal, created</p>
            <div className="col-lg-12 text-center modal-btn">
              <Link
                to="#"
                className="btn btn-light"
                onClick={() => setOpenModal(false)}
              >
                Cancel
              </Link>
              <Link to={route.deals} className="btn btn-primary">
                View All Deals
              </Link>
            </div>
          </div>
        </div>
      </Modal>
      {/* /Create Deal Success Modal */}
      {/* Add New Pipeline */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_pipeline"
        style={{ width: "550px" }}
      >
        <div className="offcanvas-header border-bottom">
          <h4>Add New Pipeline</h4>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form>
            <div>
              <div className="mb-3">
                <label className="col-form-label">
                  Pipeline Name <span className="text-danger">*</span>
                </label>
                <input className="form-control" type="text" />
              </div>
              <div className="mb-3">
                <div className="pipe-title d-flex align-items-center justify-content-between">
                  <h5 className="form-title">Pipeline Stages</h5>
                  <Link
                    to="#"
                    className="add-stage"
                    data-bs-toggle="modal"
                    data-bs-target="#add_stage"
                  >
                    <i className="ti ti-square-rounded-plus" />
                    Add New
                  </Link>
                </div>
                <div className="pipeline-listing">
                  <div className="pipeline-item">
                    <p>
                      <i className="ti ti-grip-vertical" /> Inpipeline
                    </p>
                    <div className="action-pipeline">
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#edit_stage"
                      >
                        <i className="ti ti-edit text-blue" />
                        Edit
                      </Link>
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#delete_stage"
                      >
                        <i className="ti ti-trash text-danger" />
                        Delete
                      </Link>
                    </div>
                  </div>
                  <div className="pipeline-item">
                    <p>
                      <i className="ti ti-grip-vertical" /> Follow Up
                    </p>
                    <div className="action-pipeline">
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#edit_stage"
                      >
                        <i className="ti ti-edit text-blue" />
                        Edit
                      </Link>
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#delete_stage"
                      >
                        <i className="ti ti-trash text-danger" />
                        Delete
                      </Link>
                    </div>
                  </div>
                  <div className="pipeline-item">
                    <p>
                      <i className="ti ti-grip-vertical" /> Schedule Service
                    </p>
                    <div className="action-pipeline">
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#edit_stage"
                      >
                        <i className="ti ti-edit text-blue" />
                        Edit
                      </Link>
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#delete_stage"
                      >
                        <i className="ti ti-trash text-danger" />
                        Delete
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <h5 className="form-title">Access</h5>
                <div className="d-flex flex-wrap access-item nav">
                  <div
                    className="radio-btn"
                    data-bs-toggle="tab"
                    data-bs-target="#all"
                  >
                    <input
                      type="radio"
                      className="status-radio"
                      id="all"
                      name="status"
                      defaultChecked
                    />
                    <label htmlFor="all">All</label>
                  </div>
                  <div
                    className="radio-btn"
                    data-bs-toggle="tab"
                    data-bs-target="#select-person"
                  >
                    <input
                      type="radio"
                      className="status-radio"
                      id="select"
                      name="status"
                    />
                    <label htmlFor="select">Select Person</label>
                  </div>
                </div>
                <div className="tab-content mb-3">
                  <div className="tab-pane fade" id="select-person">
                    <div className="access-wrapper">
                      <div className="access-view">
                        <div className="access-img">
                          <ImageWithBasePath
                            src="assets/img/profiles/avatar-21.jpg"
                            alt="Image"
                          />
                          Vaughan
                        </div>
                        <Link to="#">Remove</Link>
                      </div>
                      <div className="access-view">
                        <div className="access-img">
                          <ImageWithBasePath
                            src="assets/img/profiles/avatar-01.jpg"
                            alt="Image"
                          />
                          Jessica
                        </div>
                        <Link to="#">Remove</Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                data-bs-dismiss="offcanvas"
                className="btn btn-light me-2"
              >
                Cancel
              </button>
              <button type="button" className="btn btn-primary">
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* /Add New Pipeline */}
      {/* Delete Stage */}
      <div className="modal fade" id="delete_stage" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Remove Stage?</h4>
                <p className="mb-0">
                  Are you sure you want to remove <br /> stage you selected.
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <Link
                    to="#"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <Link
                    to={route.deals}
                    data-bs-dismiss="modal"
                    className="btn btn-danger"
                  >
                    Yes, Delete it
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Delete Stage */}
      {/* Add New Stage */}
      <div className="modal custom-modal fade" id="add_stage" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add New Stage</h5>
              <button
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <form>
                <div className="mb-3">
                  <label className="col-form-label">Stage Name *</label>
                  <input type="text" className="form-control" />
                </div>
                <div className="modal-btn text-end">
                  <Link
                    to="#"
                    className="btn btn-light"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="button"
                    data-bs-dismiss="modal"
                    className="btn btn-danger"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* /Add New Stage */}
      {/* Edit Stage */}
      <div className="modal custom-modal fade" id="edit_stage" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Stage</h5>
              <button
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <form>
                <div className="mb-3">
                  <label className="col-form-label">Stage Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    defaultValue="Inpipeline"
                  />
                </div>
                <div className="modal-btn text-end">
                  <Link
                    to="#"
                    className="btn btn-light"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="button"
                    data-bs-dismiss="modal"
                    className="btn btn-danger"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* /Edit Stage */}
      {/* Add New View */}
      <div className="modal custom-modal fade" id="save_view" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add New View</h5>
              <button
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <form>
                <div className="mb-3">
                  <label className="col-form-label">View Name</label>
                  <input type="text" className="form-control" />
                </div>
                <div className="modal-btn text-end">
                  <Link
                    to="#"
                    className="btn btn-light"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <button
                    type="button"
                    data-bs-dismiss="modal"
                    className="btn btn-danger"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>{" "}
      {/* /Add New View */}
      {/* Bulk Delete Confirmation Modal */}

      {detailsDeal && (
        <EntityDetailsModal
          isOpen={!!detailsDeal}
          onClose={() => setDetailsDeal(null)}
          title={detailsDeal.title}
          subtitle={detailsDeal.company?.company_name || undefined}
          eyebrow={getLifecycleTypeName("DEAL")}
          avatarIcon="ti ti-medal"
          badge={{
            label: detailsDeal.lifecycle_type_stage?.stage_name || "No Stage",
            tone: "info",
          }}
          sections={buildDealSections(detailsDeal)}
          onEdit={() => {
            const deal = detailsDeal;
            setDetailsDeal(null);
            handleEditClick(deal);
            openOffcanvas("offcanvas_edit");
          }}
        />
      )}

    </DealFormDataProvider>
  );
};

export default Deals;
